import { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/contexts/AuthContext";
import { AI_TOOLS } from "@/data/mockPeople";

export default function MarkSolvedModal({
  open,
  onOpenChange,
  problem,
  submissions,
  onSolved,
}) {
  const [mode, setMode] = useState("submission"); // "submission" | "external"
  const [submissionId, setSubmissionId] = useState("");
  const [externalId, setExternalId] = useState("");
  const [artefact, setArtefact] = useState("");
  const [tools, setTools] = useState([]);
  const [busy, setBusy] = useState(false);

  const toggleTool = (t) =>
    setTools(tools.includes(t) ? tools.filter((x) => x !== t) : [...tools, t]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload =
        mode === "submission"
          ? { submission_id: submissionId, tools, models: tools }
          : {
              external_solver_user_id: externalId.trim(),
              artefact: artefact.trim(),
              tools,
              models: tools,
            };
      if (mode === "submission" && !submissionId) {
        toast.error("Pick a submission first.");
        setBusy(false);
        return;
      }
      if (mode === "external") {
        if (!externalId.trim()) {
          toast.error("Enter the solver's user ID.");
          setBusy(false);
          return;
        }
        if (!artefact.trim()) {
          toast.error("A writeup is required for off-platform solutions.");
          setBusy(false);
          return;
        }
      }
      const { data } = await api.post(
        `/open-problems/${problem.problem_id}/mark-solved`,
        payload
      );
      onSolved(data);
      onOpenChange(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't mark solved.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-white p-8 overflow-y-auto"
      >
        <SheetHeader className="text-left">
          <SheetTitle className="font-display text-2xl">
            Mark as solved
          </SheetTitle>
          <SheetDescription className="text-[#4B5563]">
            The solution artefact will attach to the solver's profile and this
            problem will move to closed.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 grid grid-cols-2 gap-2 p-1 rounded-full bg-[#F3F4F6]">
          {[
            { k: "submission", l: "From submissions" },
            { k: "external", l: "Off-platform" },
          ].map((tab) => (
            <button
              key={tab.k}
              type="button"
              onClick={() => setMode(tab.k)}
              data-testid={`ms-tab-${tab.k}`}
              className={`h-9 rounded-full text-sm font-medium transition-colors ${
                mode === tab.k
                  ? "bg-white text-[#111827] shadow-sm"
                  : "text-[#4B5563]"
              }`}
            >
              {tab.l}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="mt-6 space-y-4">
          {mode === "submission" ? (
            submissions?.length ? (
              <ul className="space-y-2">
                {submissions.map((s) => (
                  <li key={s.submission_id}>
                    <label
                      className={`block cursor-pointer rounded-2xl p-4 ring-1 transition-colors ${
                        submissionId === s.submission_id
                          ? "ring-[#3FFFB6] bg-[#EBFFF5]"
                          : "ring-[#F3F4F6] bg-white"
                      }`}
                    >
                      <input
                        type="radio"
                        name="sub"
                        value={s.submission_id}
                        checked={submissionId === s.submission_id}
                        onChange={() => setSubmissionId(s.submission_id)}
                        data-testid={`ms-pick-${s.submission_id}`}
                        className="sr-only"
                      />
                      <p className="text-sm font-semibold text-[#111827]">
                        {s.user_name}
                      </p>
                      <p className="mt-1 text-sm text-[#4B5563] line-clamp-3">
                        {s.body}
                      </p>
                    </label>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-[#4B5563]">
                No in-app submissions yet — use the "Off-platform" tab if you
                solved this outside Oiiister.
              </p>
            )
          ) : (
            <>
              <div>
                <Label>Solver's user ID</Label>
                <Input
                  data-testid="ms-external-id"
                  value={externalId}
                  onChange={(e) => setExternalId(e.target.value)}
                  placeholder="Paste their user_id (from their profile URL)"
                  className="mt-1.5 h-11 rounded-xl"
                />
                <p className="mt-1 text-xs text-[#9CA3AF]">
                  It's the part after /people/ in their profile URL.
                </p>
              </div>
              <div>
                <Label>How was it solved? (required)</Label>
                <Textarea
                  data-testid="ms-artefact"
                  value={artefact}
                  onChange={(e) => setArtefact(e.target.value)}
                  placeholder="Walk through what they did, the tools, the outcome. This becomes the searchable artefact."
                  className="mt-1.5 min-h-32 rounded-xl"
                />
              </div>
            </>
          )}

          <div>
            <Label>AI Products used</Label>
            <div className="mt-2 flex flex-wrap gap-2 items-center">
              {[...new Set([...AI_TOOLS, ...tools])].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => toggleTool(t)}
                  className={`px-3 py-1 rounded-full text-xs font-medium ring-1 ${
                    tools.includes(t)
                      ? "bg-blue-50 text-blue-700 ring-blue-200"
                      : "bg-white text-[#4B5563] ring-[#E5E7EB]"
                  }`}
                >
                  {t}
                </button>
              ))}
              <input
                data-testid="ms-tool-custom"
                placeholder="Submit an AI Product…"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    const v = e.currentTarget.value.trim();
                    if (v && !tools.some((x) => x.toLowerCase() === v.toLowerCase())) {
                      setTools([...tools, v]);
                      e.currentTarget.value = "";
                    }
                  }
                }}
                className="text-xs h-8 rounded-full px-3 bg-white ring-1 ring-dashed ring-[#B8FFDD] focus:ring-[#3FFFB6] outline-none placeholder:text-[#9CA3AF] min-w-40"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={busy}
            data-testid="ms-submit"
            className="w-full h-12 rounded-full bg-[#3FFFB6] hover:bg-[#2DE59F] text-[#111827] font-semibold"
          >
            <BadgeCheck size={16} className="mr-2" />
            {busy ? "Marking…" : "Mark as solved"}
          </Button>
        </form>
      </SheetContent>
    </Sheet>
  );
}
